/*
  menara-univers.js — pages des collections « univers ».

  <menara-univers-ouverture> : diapositives tableau / coussin. Flèches, points,
    glissement (doigt ou souris), flèches du clavier ; léger relief au curseur.
  <menara-univers-pieces> : carrousel des pièces, filtres Tout / Tableaux /
    Coussins, flèches qui suivent la position du défilement.
  Apparitions : les sections montent doucement à l'entrée dans l'écran, sur
    ordinateur seulement, jamais quand moins d'animations est demandé.
*/

const MOUVEMENT_REDUIT = window.matchMedia('(prefers-reduced-motion: reduce)');
const POINTEUR_FIN = window.matchMedia('(hover: hover) and (pointer: fine)');
const ORDINATEUR = window.matchMedia('(min-width: 990px)');

class Composant extends HTMLElement {
  ecouter(cible, type, fn, options) {
    if (!cible) return;
    cible.addEventListener(type, fn, options);
    (this.nettoyeurs ||= []).push(() => cible.removeEventListener(type, fn, options));
  }

  disconnectedCallback() {
    (this.nettoyeurs || []).forEach((f) => f());
    this.nettoyeurs = [];
    this.demarre = false;
  }
}

/* ─────────────── Ouverture ─────────────── */

class MenaraUniversOuverture extends Composant {
  connectedCallback() {
    if (this.demarre) return;
    this.demarre = true;
    this.diapos = Array.from(this.querySelectorAll('[data-muv-diapo]'));
    this.points = Array.from(this.querySelectorAll('[data-muv-point]'));
    this.annonce = this.querySelector('[data-muv-annonce]');
    this.index = Math.max(0, this.diapos.findIndex((d) => d.classList.contains('est-active')));

    this.brancherRelief();
    if (this.diapos.length < 2) return;

    this.ecouter(this.querySelector('[data-muv-prec]'), 'click', () => this.aller(this.index - 1, -1));
    this.ecouter(this.querySelector('[data-muv-suiv]'), 'click', () => this.aller(this.index + 1, 1));
    this.points.forEach((p) =>
      this.ecouter(p, 'click', () => {
        const i = Number(p.dataset.muvPoint);
        this.aller(i, i >= this.index ? 1 : -1);
      })
    );
    this.ecouter(this, 'keydown', (e) => {
      if (e.key === 'ArrowLeft') this.aller(this.index - 1, -1);
      else if (e.key === 'ArrowRight') this.aller(this.index + 1, 1);
    });
    this.brancherGlissement();

    // Éditeur de thème : choisir un bloc « Paire d'images » l'affiche.
    this.ecouter(document, 'shopify:block:select', (e) => {
      const i = this.diapos.indexOf(e.target);
      if (i >= 0) this.aller(i, i >= this.index ? 1 : -1, false);
    });
  }

  aller(cible, sens = 1, annoncer = true) {
    const n = this.diapos.length;
    const i = ((cible % n) + n) % n;
    if (i === this.index) return;
    const ancienne = this.diapos[this.index];
    const nouvelle = this.diapos[i];
    this.style.setProperty('--muv-sens', String(sens));

    ancienne.classList.remove('est-active');
    ancienne.classList.add('est-sortante');
    ancienne.setAttribute('aria-hidden', 'true');
    nouvelle.classList.remove('est-sortante');
    // Le navigateur doit peindre l'état de départ avant la transition.
    nouvelle.getBoundingClientRect();
    nouvelle.classList.add('est-active');
    nouvelle.removeAttribute('aria-hidden');
    clearTimeout(this.minuteurSortie);
    this.minuteurSortie = setTimeout(() => ancienne.classList.remove('est-sortante'), MOUVEMENT_REDUIT.matches ? 0 : 1300);

    this.index = i;
    this.points.forEach((p, k) => p.setAttribute('aria-current', k === i ? 'true' : 'false'));
    if (annoncer && this.annonce) this.annonce.textContent = `Visuel ${i + 1} sur ${n}`;
  }

  brancherGlissement() {
    const scene = this.querySelector('[data-muv-scene]');
    let depart = null;
    this.ecouter(scene, 'pointerdown', (e) => {
      if (e.button !== 0) return;
      depart = { x: e.clientX, y: e.clientY, id: e.pointerId };
    });
    this.ecouter(scene, 'pointermove', (e) => {
      if (!depart || e.pointerId !== depart.id || e.pointerType !== 'mouse') return;
      if (Math.abs(e.clientX - depart.x) > 8) this.classList.add('est-glisse');
    });
    const finir = (e) => {
      if (!depart || e.pointerId !== depart.id) return;
      const dx = e.clientX - depart.x;
      const dy = e.clientY - depart.y;
      depart = null;
      this.classList.remove('est-glisse');
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.2) {
        if (dx < 0) this.aller(this.index + 1, 1);
        else this.aller(this.index - 1, -1);
      }
    };
    this.ecouter(scene, 'pointerup', finir);
    this.ecouter(scene, 'pointercancel', () => {
      depart = null;
      this.classList.remove('est-glisse');
    });
    this.ecouter(scene, 'dragstart', (e) => e.preventDefault());
  }

  /** Relief au curseur : les deux plans glissent de quelques pixels en sens contraire. */
  brancherRelief() {
    let image = 0;
    let x = 0;
    let y = 0;
    const peindre = () => {
      image = 0;
      this.style.setProperty('--muv-px', x.toFixed(3));
      this.style.setProperty('--muv-py', y.toFixed(3));
    };
    this.ecouter(this, 'pointermove', (e) => {
      if (e.pointerType !== 'mouse' || !POINTEUR_FIN.matches || MOUVEMENT_REDUIT.matches) return;
      const r = this.getBoundingClientRect();
      x = ((e.clientX - r.left) / r.width - 0.5) * 2;
      y = ((e.clientY - r.top) / r.height - 0.5) * 2;
      if (!image) image = requestAnimationFrame(peindre);
    });
    this.ecouter(this, 'pointerleave', () => {
      x = 0;
      y = 0;
      if (!image) image = requestAnimationFrame(peindre);
    });
    (this.nettoyeurs ||= []).push(() => cancelAnimationFrame(image));
  }
}

/* ─────────────── Les pièces ─────────────── */

class MenaraUniversPieces extends Composant {
  connectedCallback() {
    if (this.demarre) return;
    this.demarre = true;
    this.piste = this.querySelector('[data-muv-piste]');
    this.cartes = Array.from(this.querySelectorAll('[data-muv-carte]'));
    this.filtres = Array.from(this.querySelectorAll('[data-muv-filtre]'));
    this.fleches = this.querySelector('[data-muv-fleches]');
    this.prec = this.querySelector('[data-muv-prec]');
    this.suiv = this.querySelector('[data-muv-suiv]');
    this.compte = this.querySelector('[data-muv-compte]');
    if (!this.piste) return;

    this.filtres.forEach((b) => this.ecouter(b, 'click', () => this.filtrer(b.dataset.muvFiltre)));
    this.ecouter(this.prec, 'click', () => this.defiler(-1));
    this.ecouter(this.suiv, 'click', () => this.defiler(1));

    let image = 0;
    const planifier = () => {
      if (!image) image = requestAnimationFrame(() => {
        image = 0;
        this.majFleches();
      });
    };
    this.ecouter(this.piste, 'scroll', planifier, { passive: true });
    this.ecouter(window, 'resize', planifier, { passive: true });
    (this.nettoyeurs ||= []).push(() => cancelAnimationFrame(image));
    this.majFleches();
  }

  filtrer(famille) {
    this.filtres.forEach((b) => b.setAttribute('aria-pressed', b.dataset.muvFiltre === famille ? 'true' : 'false'));
    let visibles = 0;
    this.cartes.forEach((c) => {
      const garder = famille === 'tout' || c.dataset.categorie === famille;
      c.hidden = !garder;
      if (garder) visibles += 1;
    });
    this.piste.scrollTo({ left: 0, behavior: 'auto' });
    this.majFleches();
    if (this.compte) {
      const mot = visibles > 1 ? this.dataset.libellePlusieurs : this.dataset.libelleUne;
      this.compte.textContent = `${visibles} ${mot}`;
    }
  }

  defiler(sens) {
    const pas = Math.max(this.piste.clientWidth * 0.85, 200);
    this.piste.scrollBy({ left: sens * pas, behavior: MOUVEMENT_REDUIT.matches ? 'auto' : 'smooth' });
  }

  majFleches() {
    if (!this.fleches) return;
    const max = this.piste.scrollWidth - this.piste.clientWidth;
    const utile = max > 4;
    this.fleches.hidden = !utile;
    if (!utile) return;
    this.prec.disabled = this.piste.scrollLeft <= 4;
    this.suiv.disabled = this.piste.scrollLeft >= max - 4;
  }
}

/* ─────────────── Apparitions ─────────────── */

let observateur = null;

function apparitions(racine = document) {
  const elements = racine.querySelectorAll('[data-muv-apparition]:not(.est-visible)');
  if (!elements.length) return;
  if (!ORDINATEUR.matches || MOUVEMENT_REDUIT.matches || !('IntersectionObserver' in window)) {
    elements.forEach((el) => el.classList.add('est-visible'));
    return;
  }
  document.documentElement.classList.add('muv-anime');
  observateur ||= new IntersectionObserver(
    (entrees) => {
      entrees.forEach((entree) => {
        if (!entree.isIntersecting) return;
        entree.target.classList.add('est-visible');
        observateur.unobserve(entree.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );
  elements.forEach((el) => observateur.observe(el));
  surveillerDefilement();
}

// Filet de sécurité : le thème fait défiler un conteneur interne sur ordinateur.
// Une mesure au défilement (une par image) révèle ce que l'observateur aurait
// manqué ; rien ne peut rester caché.
let surveille = false;
function surveillerDefilement() {
  if (surveille) return;
  surveille = true;
  let image = 0;
  const verifier = () => {
    image = 0;
    const restants = document.querySelectorAll('[data-muv-apparition]:not(.est-visible)');
    restants.forEach((el) => {
      if (el.getBoundingClientRect().top < window.innerHeight * 0.94) el.classList.add('est-visible');
    });
    if (!restants.length) {
      document.removeEventListener('scroll', planifier, true);
      surveille = false;
    }
  };
  const planifier = () => {
    if (!image) image = requestAnimationFrame(verifier);
  };
  document.addEventListener('scroll', planifier, { capture: true, passive: true });
  setTimeout(verifier, 1200);
}

if (!customElements.get('menara-univers-ouverture')) {
  customElements.define('menara-univers-ouverture', MenaraUniversOuverture);
}
if (!customElements.get('menara-univers-pieces')) {
  customElements.define('menara-univers-pieces', MenaraUniversPieces);
}

apparitions();
document.addEventListener('shopify:section:load', (e) => apparitions(e.target));
